import * as React from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Collapse,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useRef } from "react";

const OPEN_DELAY = 80;
const CLOSE_DELAY = 140;

const fonts = {
  EN: "LINE Seed Sans",
  ZH: "LINE Seed TW",
  JP: "LINE Seed JP",
};

export default function DesktopMegaMenu({
  mode,
  rootItem,
  buttonSx,
  primaryColor = "#1976d2",
  defaultColor = "text.secondary",
  isLoggedIn = false,
  onRequireLogin = () => {},
  active = false,
  topAdjust = 0,
  lang,
}) {
  const [open, setOpen] = React.useState(false);
  const [panelTop, setPanelTop] = React.useState(0);
  const [panelMaxH, setPanelMaxH] = React.useState(560);

  const PANEL_ITEM_FONT_SIZE = { lg: 16, md: 15, sm: 14 };
  const ITEM_VERTICAL_PADDING = 0.9;

  const triggerRef = useRef(null);

  const [openByLevel, setOpenByLevel] = React.useState({});
  const isLevelOpen = (lvl, key) => openByLevel[lvl] === key;
  const toggleLevelKey = (lvl, key) =>
    setOpenByLevel((prev) => {
      const next = { ...prev };
      if (next[lvl] === key) {
        delete next[lvl];
      } else {
        next[lvl] = key;
      }
      Object.keys(next).forEach((k) => {
        const n = Number(k);
        if (!Number.isNaN(n) && n > lvl) delete next[k];
      });
      return next;
    });

  const timers = useRef({ open: null, close: null });
  const clearTimers = () => {
    if (timers.current.open) clearTimeout(timers.current.open);
    if (timers.current.close) clearTimeout(timers.current.close);
    timers.current.open = null;
    timers.current.close = null;
  };

  const computePanelMetrics = (rectBottomPx) => {
    const top = Math.round(rectBottomPx) + topAdjust;
    setPanelTop(top);
    const vh = window.innerHeight || 0;
    const BOTTOM_MARGIN = 8;
    const maxH = Math.max(320, vh - top - BOTTOM_MARGIN);
    setPanelMaxH(maxH);
  };

  const getPanelAnchorBottom = () => {
    const headerRect =
      triggerRef.current?.closest?.("header")?.getBoundingClientRect?.();

    if (headerRect) {
      return headerRect.bottom;
    }

    const triggerRect = triggerRef.current?.getBoundingClientRect?.();
    return triggerRect ? triggerRect.bottom : 0;
  };

  const scheduleOpen = () => {
    clearTimers();
    timers.current.open = setTimeout(() => {
      computePanelMetrics(getPanelAnchorBottom());
      setOpen(true);
    }, OPEN_DELAY);
  };

  const scheduleClose = () => {
    clearTimers();
    timers.current.close = setTimeout(() => {
      setOpen(false);
      setOpenByLevel({});
    }, CLOSE_DELAY);
  };

  const cancelClose = () => {
    if (timers.current.close) {
      clearTimeout(timers.current.close);
      timers.current.close = null;
    }
  };

  const closeNow = () => {
    clearTimers();
    setOpen(false);
    setOpenByLevel({});
  };

  React.useEffect(() => {
    if (!open) return;
    const handler = () => {
      computePanelMetrics(getPanelAnchorBottom());
    };
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, { passive: true });
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, topAdjust]);

  const requiresLogin = (node) => node?.login === "required";
  const rootLocked = !isLoggedIn && requiresLogin(rootItem);

  const onRootEnter = () => {
    if (rootLocked) return;
    cancelClose();
    scheduleOpen();
  };

  const onRootClick = (e) => {
    if (rootLocked) {
      e.preventDefault();
      onRequireLogin(rootItem.href);
      return;
    }
    if (!open) scheduleOpen();
  };

  // B2B-only routing. The B2C website has been separated.
  const rootHref = String(rootItem?.href || "");
  const modePrefix = "";

  const baseLabel = "商品資訊";
  const productInfoBase = "/商品資訊";

  const isProductRoot =
    String(rootItem?.label || "") === baseLabel ||
    new RegExp(`${baseLabel}`).test(rootHref);

  const location = useLocation();

  const safeDecode = (value = "") => {
    const text = String(value || "");

    try {
      return decodeURIComponent(text);
    } catch {
      return text;
    }
  };

  const normalizePath = (path = "") =>
    safeDecode(path)
      .replace(/[?#].*$/, "")
      .replace(/\/+$/, "") || "/";

  const routeStartsWith = (href = "", pathname = "") => {
    const normalizedHref = normalizePath(href);
    const normalizedPathname = normalizePath(pathname);

    return (
      normalizedPathname === normalizedHref ||
      normalizedPathname.startsWith(`${normalizedHref}/`)
    );
  };

  const currentPathname = normalizePath(location.pathname);
  const currentSearchParams = new URLSearchParams(location.search);
  const rawCurrentCategory = currentSearchParams.get("cat") || "";
  const currentCategory = rawCurrentCategory
    ? normalizePath(rawCurrentCategory)
    : "";
  const currentTab = Number(location.state?.tab ?? -1);

  const isExternalHref = (href = "") => {
    const raw = String(href || "").trim();
    return raw.startsWith("http://") || raw.startsWith("https://");
  };

  const isMenuItemActive = (href = "") => {
    if (!href || isExternalHref(href)) {
      return false;
    }

    const normalizedHref = normalizePath(href);

    /*
     * Company profile page has tabs. If tab 1 is active, highlight
     * Vision & Philosophy. Otherwise highlight Profile.
     */
    if (currentPathname === "/公司簡介") {
      if (normalizedHref === "/企業理念") {
        return currentTab === 1;
      }

      if (normalizedHref === "/公司簡介") {
        return currentTab !== 1;
      }
    }

    if (isProductRoot && currentCategory) {
      return (
        normalizedHref === currentCategory ||
        routeStartsWith(normalizedHref, currentCategory) ||
        routeStartsWith(currentCategory, normalizedHref)
      );
    }

    return routeStartsWith(normalizedHref, currentPathname);
  };

  const toForCategory = (href = "") => {
    if (!href) return productInfoBase;
    const search = `?cat=${encodeURIComponent(href)}`;
    return `${productInfoBase}${search}`;
  };

  const tokens = {
    triggerText: defaultColor,
    triggerHoverBg: "transparent",
    triggerOpenBg: "transparent",
    triggerHoverText: primaryColor,

    panelBg: "#fffffc",
    panelTopRule: "divider",
    panelShadow: "0 10px 28px rgba(0,0,0,0.10)",
    panelText: primaryColor,
    panelSubtle: "text.secondary",

    colHeaderBg: "#fffffc",
    colHeaderText: primaryColor,
    dividerColor: "divider",
    leafHoverBg: primaryColor,
    leafHoverText: "#ffffff",
    caretActive: primaryColor,
  };

  const children = rootItem.children ?? [];
  let sectionGroups;

  if (isProductRoot && children.length >= 4) {
    const left = children.slice(0, 2);
    const right = children.slice(2);
    sectionGroups = [left, right];
  } else {
    const total = children.length;
    const perCol = Math.ceil(total / 3) || 1;
    sectionGroups = [
      children.slice(0, perCol),
      children.slice(perCol, perCol * 2),
      children.slice(perCol * 2),
    ];
  }

  const Leaf = ({ label, href, locked, external }) => {
    const isQAPage = label?.includes("常見問題");
    const isExternal = !!external;

    // ✅ B2B philosophy routing: no "/b2b" anymore
    const isB2BPhilosophy =
      modePrefix === "" &&
      ((href && (href === "/企業理念" || href === "企業理念")) ||
        label?.includes("企業理念"));

    const activeLeaf = isMenuItemActive(href);

    const commonProps = {
      sx: {
        borderRadius: 1,
        py: ITEM_VERTICAL_PADDING,
        px: 1.25,
        bgcolor: activeLeaf && !locked ? tokens.leafHoverBg : "transparent",
        color: activeLeaf && !locked ? tokens.leafHoverText : tokens.panelText,
        opacity: locked ? 0.65 : 1,
        transition: "background-color .18s ease, color .18s ease",
        "&:hover, &:focus-visible": {
          bgcolor: locked ? "transparent" : tokens.leafHoverBg,
          color: locked ? tokens.panelText : tokens.leafHoverText,
        },
      },
      onClick: (e) => {
        if (locked) {
          e.preventDefault();
          onRequireLogin(href);
          return;
        }
        closeNow();
      },
    };

    // ✅ external leaf
    if (isExternal && !locked) {
      return (
        <ListItemButton
          component="a"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          {...commonProps}
          sx={{
            ...commonProps.sx,
            textDecoration: "none",
          }}
        >
          <ListItemText
            primary={
              <Box
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.6 }}
              >
                <span>{label}</span>
                <OpenInNewRoundedIcon sx={{ fontSize: 18, opacity: 0.9 }} />
              </Box>
            }
            primaryTypographyProps={{
              fontSize: PANEL_ITEM_FONT_SIZE,
              fontFamily: fonts[lang],
              lineHeight: 1.4,
              color: "inherit",
            }}
          />
        </ListItemButton>
      );
    }

    return (
      <ListItemButton
        component={locked ? "div" : RouterLink}
        to={
          locked
            ? undefined
            : isB2BPhilosophy
            ? "/公司簡介"
            : isQAPage
            ? `${modePrefix}/常見問題Q&A`
            : isProductRoot
            ? toForCategory(href)
            : href
        }
        state={
          locked
            ? undefined
            : isB2BPhilosophy
            ? { tab: 1 }
            : isProductRoot && href
            ? { categoryHref: href }
            : undefined
        }
        {...commonProps}
      >
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            fontSize: PANEL_ITEM_FONT_SIZE,
            fontFamily: fonts[lang],
            lineHeight: 1.4,
            color: "inherit",
          }}
        />
      </ListItemButton>
    );
  };

  const NodeWithChildren = ({ node, level = 0, ancestorLocked = false }) => {
    const thisLocked = !isLoggedIn && (requiresLogin(node) || ancestorLocked);
    const key = `${level}|${node.label}`;
    const opened = isLevelOpen(level, key);
    const hasChildren = Boolean(node.children?.length);

    // ✅ B2B philosophy node: no "/b2b" anymore
    const isB2BPhilosophyNode =
      (node.href && (node.href === "/企業理念" || node.href === "企業理念")) ||
      node.label?.includes("企業理念");

    const activeNode = isMenuItemActive(node.href);

    return (
      <Box sx={{ mb: 0.25 }}>
        <Box sx={{ display: "flex", alignItems: "stretch" }}>
          <ListItemButton
            component={thisLocked || !node.href ? "div" : RouterLink}
            to={
              thisLocked || !node.href
                ? undefined
                : isB2BPhilosophyNode
                ? "/公司簡介"
                : isProductRoot
                ? toForCategory(node.href)
                : node.href
            }
            state={
              thisLocked || !node.href
                ? undefined
                : isB2BPhilosophyNode
                ? { tab: 1 }
                : isProductRoot
                ? { categoryHref: node.href }
                : undefined
            }
            onClick={(e) => {
              if (thisLocked) {
                e.preventDefault();
                onRequireLogin(node.href);
                return;
              }
              closeNow();
            }}
            sx={{
              flex: 1,
              borderRadius: 1.25,
              py: ITEM_VERTICAL_PADDING,
              px: 1.25,
              bgcolor: activeNode && !thisLocked ? tokens.leafHoverBg : "transparent",
              color: activeNode && !thisLocked ? tokens.leafHoverText : tokens.panelText,
              opacity: thisLocked ? 0.65 : 1,
              transition: "background-color .18s ease, color .18s ease",
              "&:hover, &:focus-visible": {
                bgcolor: thisLocked ? "transparent" : tokens.leafHoverBg,
                color: thisLocked ? tokens.panelText : tokens.leafHoverText,
              },
            }}
          >
            <ListItemText
              primary={node.label}
              primaryTypographyProps={{
                fontSize: PANEL_ITEM_FONT_SIZE,
                fontFamily: fonts[lang],
                lineHeight: 1.4,
                color: "inherit",
              }}
            />
          </ListItemButton>

          {hasChildren && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (thisLocked) {
                  onRequireLogin(node.href);
                  return;
                }
                toggleLevelKey(level, key);
              }}
              sx={{
                ml: 0.5,
                opacity: thisLocked ? 0.65 : 1,
                color: tokens.panelText,
                p: 0.5,
                alignSelf: "stretch",
              }}
            >
              {opened ? (
                <ExpandMoreRounded sx={{ color: tokens.caretActive }} />
              ) : (
                <ChevronRightRounded sx={{ color: tokens.panelText }} />
              )}
            </IconButton>
          )}
        </Box>

        {hasChildren && (
          <Collapse in={!thisLocked && opened} timeout="auto" unmountOnExit>
            <List disablePadding sx={{ pl: 4, pb: 0.25 }}>
              {node.children.map((leaf) =>
                leaf.children?.length ? (
                  <NodeWithChildren
                    key={`node-${level + 1}-${leaf.label}`}
                    node={leaf}
                    level={level + 1}
                    ancestorLocked={thisLocked || ancestorLocked}
                  />
                ) : (
                  <Leaf
                    key={`leaf-${level + 1}-${leaf.label}`}
                    label={leaf.label}
                    href={leaf.href}
                    external={leaf.external}
                    locked={thisLocked || ancestorLocked}
                  />
                )
              )}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  const renderSection = (group) => (
    <List disablePadding>
      {group.map((item) =>
        item.children?.length ? (
          <NodeWithChildren
            key={`node-0-${item.label}`}
            node={item}
            level={0}
            ancestorLocked={rootLocked}
          />
        ) : (
          <Leaf
            key={`leaf-0-${item.label}`}
            label={item.label}
            href={item.href}
            external={item.external}
            locked={rootLocked}
          />
        )
      )}
    </List>
  );

  const sidebarWidth = { lg: "200px", md: "190px", sm: "170px" };
  const panelPaddingY = { xs: 0, sm: 0, md: 0 };
  const colMin = 240;
  const colCount = sectionGroups.length || 1;

  return (
    <ClickAwayListener onClickAway={closeNow}>
      <Box
        sx={{ position: "relative", display: "inline-flex" }}
        onMouseLeave={scheduleClose}
      >
        <Button
          ref={triggerRef}
          component={rootItem.href && !rootLocked ? RouterLink : "button"}
          to={rootItem.href && !rootLocked ? rootItem.href : undefined}
          color="inherit"
          endIcon={
            <ArrowDropDownIcon
              sx={{ fontSize: 20, color: "inherit" }}
            />
          }
          onMouseEnter={onRootEnter}
          onClick={onRootClick}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          sx={{
            ...buttonSx,
            minHeight: 48,
            color:
              active || (open && !rootLocked)
                ? tokens.triggerHoverText
                : tokens.triggerText,
            bgcolor: "transparent",
            "&:hover, &:focus-visible": {
              bgcolor: "transparent",
              color: rootLocked ? tokens.triggerText : tokens.triggerHoverText,
            },
            borderRadius: 1.5,
            opacity: rootLocked ? 0.75 : 1,
            fontSize: { lg: 18, md: 16 },
            position: "relative",
            transition: "color .18s ease",
            "&::after": {
              content: '""',
              position: "absolute",
              left: 8,
              right: 8,
              bottom: 2,
              height: 2,
              backgroundColor: "currentColor",
              transform: active ? "scaleX(1)" : "scaleX(0)",
              transformOrigin: "center",
              transition: "transform .18s ease",
            },
          }}
        >
          {rootItem.label}
        </Button>

        {open && !rootLocked && (
          <Box
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            sx={{
              position: "fixed",
              left: 0,
              right: 0,
              top: `${panelTop}px`,
              transform: "none",
              zIndex: (t) => t.zIndex.modal,
            }}
          >
            <Paper
              elevation={8}
              square
              sx={{
                width: "100%",
                borderRadius: 0,
                borderTop: "1px solid",
                borderColor: "divider",
                bgcolor: tokens.panelBg,
                color: tokens.panelText,
                boxShadow: tokens.panelShadow,
                maxHeight: panelMaxH,
                overflowY: "auto",
                overscrollBehavior: "contain",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 1280,
                  mx: "auto",
                  px: { xs: 2, md: 3 },
                  py: panelPaddingY,
                }}
              >
                <Box
                  sx={{
                    display: "block",
                  }}
                >
                  <Box
                    sx={{
                      gridColumn: "1 / -1",
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          sm: `${sidebarWidth.sm} 1fr`,
                          md: `${sidebarWidth.md} 1fr`,
                          lg: `${sidebarWidth.lg} 1fr`,
                        },
                        columnGap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          p: { md: 2.5, sm: 2 },
                          bgcolor: tokens.colHeaderBg,
                          borderRight: "1px dashed",
                          borderColor: tokens.dividerColor,
                          display: "flex",
                          alignItems: "flex-start",
                          color: tokens.colHeaderText,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            letterSpacing: 0.2,
                            color: "inherit",
                            fontSize: { lg: 35, md: 19, sm: 18 },
                            fontFamily: fonts[lang],
                          }}
                        >
                          {rootItem.label}
                        </Typography>
                      </Box>

                      <Box sx={{ p: { md: 1, sm: 1 } }}>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: `repeat(${colCount}, minmax(${colMin}px, 1fr))`,
                            columnGap: 2,
                          }}
                        >
                          {sectionGroups.map((group, i) => (
                            <Box
                              key={`sec-${i}`}
                              sx={{
                                px: 1.5,
                                ...(i > 0 && {
                                  borderLeft: "1px dashed",
                                  borderColor: tokens.dividerColor,
                                }),
                              }}
                            >
                              {renderSection(group)}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
}
